import { useState, useEffect, useCallback } from 'react';
import Joyride, { CallBackProps, STATUS, ACTIONS, EVENTS, Step } from 'react-joyride';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

const tourSteps: Step[] = [
  {
    target: 'body',
    content: (
      <div className="text-center py-2">
        <div className="text-4xl mb-3">🎉</div>
        <h2 className="text-xl font-bold mb-2">Welcome to TAM Store!</h2>
        <p className="text-sm text-muted-foreground">Let's take a quick tour to help you get started. It'll only take a minute.</p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="sidebar"]',
    content: (
      <div>
        <h3 className="font-bold mb-1">📍 Navigation Sidebar</h3>
        <p className="text-sm">This is your main navigation. Access the Dashboard, All Tools, and browse tool categories like AI, PDF, Developer, and more.</p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour="all-tools"]',
    content: (
      <div>
        <h3 className="font-bold mb-1">🔧 All Tools</h3>
        <p className="text-sm">Browse every available tool in one place. Filter by category — AI, PDF, Developer, Design, Utilities, and more.</p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour="my-sites"]',
    content: (
      <div>
        <h3 className="font-bold mb-1">🌐 My Sites</h3>
        <p className="text-sm">All sites you've published from Code Studio appear here. Edit, manage, or view analytics for each site.</p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour="code-studio"]',
    content: (
      <div>
        <h3 className="font-bold mb-1">💻 Code Studio</h3>
        <p className="text-sm">Our powerful web IDE. Build HTML/CSS/JS projects with a live preview, AI assistant, and one-click publishing.</p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="guide"]',
    content: (
      <div>
        <h3 className="font-bold mb-1">📖 Guide</h3>
        <p className="text-sm">Need help? The Guide has detailed walkthroughs, FAQs, and tips for every tool on the platform.</p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour="profile"]',
    content: (
      <div>
        <h3 className="font-bold mb-1">👤 Your Profile</h3>
        <p className="text-sm">Manage your account, view credits, and update your settings here.</p>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: (
      <div className="text-center py-2">
        <div className="text-4xl mb-3">🚀</div>
        <h2 className="text-xl font-bold mb-2">You're All Set!</h2>
        <p className="text-sm text-muted-foreground">Explore the tools and come back to the Guide whenever you need help. Happy building!</p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
];

const TOUR_STYLE_ID = 'joyride-tour-styles';

const OnboardingTour = () => {
  const { user } = useAuth();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!user) return;

    const checkTourStatus = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('tour_completed')
        .eq('id', user.id)
        .maybeSingle();

      if (data && !data.tour_completed) {
        setTimeout(() => setRun(true), 1500);
      }
    };

    checkTourStatus();
  }, [user]);

  const completeTour = useCallback(async () => {
    if (!user) return;
    setRun(false);
    await supabase
      .from('profiles')
      .update({ tour_completed: true })
      .eq('id', user.id);
  }, [user]);

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      completeTour();
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        setStepIndex(index + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(index - 1);
      }
    }

    if (action === ACTIONS.CLOSE) {
      completeTour();
    }
  }, [completeTour]);

  // Inject styles for prominent spotlight highlighting
  useEffect(() => {
    if (!run) {
      const existing = document.getElementById(TOUR_STYLE_ID);
      if (existing) existing.remove();
      return;
    }

    const style = document.createElement('style');
    style.id = TOUR_STYLE_ID;
    style.textContent = `
      /* Make sure overlay doesn't use mix-blend-mode which breaks on dark themes */
      .react-joyride__overlay {
        mix-blend-mode: normal !important;
      }

      /* Prominent glowing border around the spotlight cutout */
      .react-joyride__spotlight {
        border: 2px solid hsl(217, 91%, 60%) !important;
        box-shadow:
          0 0 0 4px rgba(59, 130, 246, 0.3),
          0 0 20px rgba(59, 130, 246, 0.4),
          0 0 40px rgba(59, 130, 246, 0.2) !important;
        border-radius: 14px !important;
      }

      /* Boost sidebar z-index ONLY when tour is active so spotlight can cut through */
      .joyride-active [data-tour="sidebar"] {
        z-index: 10001 !important;
      }

      /* Ensure tooltip is always on top */
      .react-joyride__tooltip {
        z-index: 10005 !important;
      }
    `;
    document.head.appendChild(style);

    // Add marker class to body
    document.body.classList.add('joyride-active');

    return () => {
      style.remove();
      document.body.classList.remove('joyride-active');
    };
  }, [run]);

  if (!run) return null;

  return (
    <Joyride
      steps={tourSteps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showSkipButton
      showProgress
      disableScrollParentFix
      spotlightPadding={10}
      callback={handleJoyrideCallback}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: 'hsl(217, 91%, 60%)',
          backgroundColor: 'hsl(222, 47%, 11%)',
          textColor: 'hsl(210, 40%, 96%)',
          overlayColor: 'rgba(0, 0, 0, 0.75)',
          arrowColor: 'hsl(222, 47%, 11%)',
        },
        tooltip: {
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid hsl(217, 91%, 60%)',
          boxShadow: '0 0 30px rgba(59, 130, 246, 0.3), 0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        },
        tooltipContent: {
          padding: '8px 0',
        },
        buttonNext: {
          backgroundColor: 'hsl(217, 91%, 60%)',
          borderRadius: '10px',
          padding: '8px 20px',
          fontSize: '14px',
          fontWeight: 600,
        },
        buttonBack: {
          color: 'hsl(210, 40%, 70%)',
          marginRight: '8px',
          fontSize: '14px',
        },
        buttonSkip: {
          color: 'hsl(210, 40%, 50%)',
          fontSize: '13px',
        },
        buttonClose: {
          color: 'hsl(210, 40%, 70%)',
        },
        spotlight: {
          borderRadius: '14px',
        },
        overlay: {
          mixBlendMode: 'normal' as any,
        },
      }}
      floaterProps={{
        disableAnimation: false,
      }}
    />
  );
};

export default OnboardingTour;
